import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ReservedStaffTimeBookingFormModal } from './ReservedStaffTimeBookingFormModal';
import { getReservedStaffTimeBookingColumns } from './reservedStaffTimeBookingColumns';
import { formatEligibleDays } from './reservedStaffTimeBookingFormat';
import { useReservedStaffTimeBookings } from '../../hooks/useReservedStaffTimeBookings';
import { useStaff } from '../../hooks/useStaff';
import type { ReservedStaffTimeRequest, ReservedStaffTimeResponse } from '../../api/generated';
import {
  BookingListSection,
  DeleteBookingModal,
  joinEntityNames,
  useBookingList
} from '../Bookings/shared';

const TABLE_MIN_WIDTH = 900;
const KEY_PREFIX = 'reserved-staff-time';

export const ReservedStaffTimeBookings = () => {
  const { t } = useTranslation();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const {
    bookings,
    isLoading,
    loadError,
    locationId,
    refresh,
    createBooking,
    updateBooking,
    deleteBooking
  } = useReservedStaffTimeBookings(routeLocationId ?? null);
  const { staff, loadError: staffLoadError, refresh: refreshStaff } = useStaff(
    routeLocationId ?? null
  );

  // The scheduler places this demand, so there is no overlap/409 path — a failure is generic.
  const list = useBookingList<ReservedStaffTimeResponse, ReservedStaffTimeRequest>({
    keyPrefix: KEY_PREFIX,
    createBooking,
    updateBooking,
    deleteBooking,
    hasConflictError: false
  });

  const staffNameById = useMemo(
    () => new Map(staff.map((member) => [member.id, member.fullName])),
    [staff]
  );

  const columns = useMemo(
    () =>
      getReservedStaffTimeBookingColumns(t, {
        staffNameById,
        onEdit: list.openEdit,
        onDelete: list.requestDelete
      }),
    [t, staffNameById, list.openEdit, list.requestDelete]
  );

  const staffLabelFor = (booking: ReservedStaffTimeResponse): string =>
    joinEntityNames(booking.staffIds, staffNameById);

  // Either the bookings or the staff list failing to load leaves the page unusable, so surface
  // whichever failed and let a single retry refetch both.
  const errorMessage = loadError ?? staffLoadError;
  const handleRetry = () => {
    refresh();
    refreshStaff().catch(() => {
      // staff load errors already surface through staffLoadError
    });
  };

  return (
    <BookingListSection
      keyPrefix={KEY_PREFIX}
      isLoading={isLoading}
      errorMessage={errorMessage}
      locationId={locationId}
      columns={columns}
      bookings={bookings}
      minWidth={TABLE_MIN_WIDTH}
      onAdd={list.openCreate}
      onRetry={handleRetry}
    >
      {list.isFormOpen && (
        <ReservedStaffTimeBookingFormModal
          booking={list.editingBooking}
          staff={staff}
          onSave={(payload) => {
            list.handleSave(payload);
          }}
          onClose={list.closeForm}
        />
      )}

      {list.deleteTarget && (
        <DeleteBookingModal
          titleKey="reserved-staff-time-delete-title"
          confirmKey="reserved-staff-time-delete-confirm"
          messageKey="reserved-staff-time-delete-message"
          recurringMessageKey="reserved-staff-time-delete-recurring-message"
          name={staffLabelFor(list.deleteTarget)}
          recurrenceSummary={
            list.deleteTarget.placement.recurrence
              ? formatEligibleDays(t, list.deleteTarget.placement.recurrence)
              : undefined
          }
          onConfirm={() => {
            list.handleConfirmDelete();
          }}
          onClose={list.cancelDelete}
        />
      )}
    </BookingListSection>
  );
};
